import { useTranslation } from '@eti/providers';
import minBy from 'lodash/minBy';
import { WALLET_PAYMENT_METHOD } from '../pages/receipt/constants/paymentProducts';
import { paymentMethodCodes, paymentMethodTypes } from '../pages/payment/constants/paymentMethods';

const { BANK, CARD, PAY_INTERNAL, WALLET } = paymentMethodTypes;
const {
  AFFIRM,
  APPLE_PAY,
  ALIPAY_CN,
  ALIPAY_HK,
  BANCONTACT,
  GOOGLE_PAY,
  IDEAL,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
  PAYPAL,
  TRUSTLY,
  UPI,
} = paymentMethodCodes;

export const usePaymentMethodTexts = () => {
  const { t } = useTranslation();

  return {
    discountText: t('Cart.Discount'),
    feeText: t('Cart.FeeText'),
    noFeeText: t('Cart.NoFeeText'),
    discountLabel: t('Payment.Cart.Discount.Label'),
    paymentMethodBankText: t('Payment.Method.Bank.Text'),
    paymentMethodBankFeeText: t('Payment.Method.Bank.Fee.Text'),
    paymentMethodCreditCardText: t('Payment.Method.CreditCard.Text'),
    paymentMethodCreditCardFeeText: t('Payment.Method.CreditCard.Fee.Text'),
    paymentMethodIDealText: t('Payment.Method.IDeal.Text'),
    paymentMethodGooglePayText: t('Payment.Method.GooglePay.Text'),
    paymentMethodBancontactText: t('Payment.Method.Bancontact.Text'),
  };
};

export const getSelectedPaymentMethodLabel = ({ type, name, price }, texts) => {
  const namePart = name ? ` (${name})` : '';
  const priceValue = price?.value || price || 0;
  const isFee = priceValue && priceValue > 0;

  switch (type) {
    case AFFIRM:
    case ALIPAY_CN:
    case ALIPAY_HK:
    case UPI:
    case WALLET:
      if (priceValue < 0) {
        return `${texts.paymentMethodBankText}${namePart}`;
      }
      return name;
    case BANK:
    case TRUSTLY:
      return isFee
        ? `${texts.paymentMethodBankFeeText}${namePart}`
        : `${texts.paymentMethodBankText}${namePart}`;
    case CARD:
      return isFee
        ? `${texts.paymentMethodCreditCardFeeText}${namePart}`
        : `${texts.paymentMethodCreditCardText}${namePart}`;
    case APPLE_PAY:
    case PAYPAL:
    case WALLET_PAYMENT_METHOD:
      return texts.productSummaryAlternativeName;
    case KLARNA_PAY_LATER:
      return texts.paymentMethodKlarnaPayLaterText;
    case KLARNA_PAY_NOW:
      return texts.paymentMethodKlarnaPayNowText;
    case KLARNA_SLICE_IT:
      return texts.paymentMethodKlarnaSliceItText;
    case KLARNA_SOFORT:
      return texts.paymentMethodKlarnaSofortText;
    case PAY_INTERNAL:
      return texts.paymentMethodPayInternalText;
    case IDEAL:
      return texts.paymentMethodIDealText;
    case BANCONTACT:
      return texts.paymentMethodBancontactText;
    case GOOGLE_PAY:
      return texts.paymentMethodGooglePayText;
    default:
      return null;
  }
};

export const getCheapestPaymentMethod = (paymentMethods) =>
  minBy(paymentMethods, (method) => method?.price ?? 0);

export const getSummaryForPaymentMethod = (method = {}, texts) => ({
  label: getSelectedPaymentMethodLabel(method, texts),
  price: method.price ?? 0,
  serviceFee: method.serviceFee ?? null,
  ...{ ...(method.type && { type: method.type }) },
});

export const useCheapestPaymentMethod = (paymentMethodPrices) => {
  const paymentMethodTexts = usePaymentMethodTexts();
  const cheapestPaymentMethod = getCheapestPaymentMethod(paymentMethodPrices);

  return getSummaryForPaymentMethod(cheapestPaymentMethod, paymentMethodTexts);
};
